import { X, Clock, Check, ArrowRight } from "lucide-react";
import { COLORS, ASSETS } from "../styles/tokens";
import { useEscapeKey, useLockBodyScroll, useSwipeToDismiss } from "../hooks";
import type { SignalPackage, SignalExample } from "../types/realsynq";

interface PackageDrawerProps {
  pkg: SignalPackage;
  onClose: () => void;
  isMobile: boolean;
  onRequestPackage?: () => void;
}

const LAYER_LABELS = { pre: "PRE-GAME", live: "IN-GAME LIVE", post: "POST-GAME" } as const;
const LAYER_COLORS = { pre: COLORS.blue, live: COLORS.cyan, post: COLORS.inkMute } as const;

function SignalRow({ s, accent }: { s: SignalExample; accent: string }) {
  const layerColor = s.layer ? LAYER_COLORS[s.layer] : accent;
  return (
    <div
      style={{
        background: "rgba(15, 26, 44, 0.8)",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {s.layer && (
            <span
              className="mono"
              style={{
                fontSize: 9,
                color: layerColor,
                letterSpacing: "0.12em",
                display: "inline-block",
                marginBottom: 4,
                padding: "2px 6px",
                borderRadius: 3,
                background: `${layerColor}14`,
                border: `1px solid ${layerColor}30`,
              }}
            >
              {s.layer.toUpperCase()}
            </span>
          )}
          <div className="mono" style={{ fontSize: 12, color: accent, marginBottom: 3 }}>
            {s.name}
          </div>
          <div style={{ fontSize: 12, color: COLORS.inkDim, lineHeight: 1.4 }}>
            {s.desc}
          </div>
        </div>
        <div
          className="mono"
          style={{
            fontSize: 11,
            color: COLORS.inkMute,
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: 4,
            flexShrink: 0,
          }}
        >
          <Clock size={11} /> {s.ttl}
        </div>
      </div>
      {(s.dealId || s.trigger) && (
        <div
          style={{
            borderTop: `1px solid ${COLORS.border}`,
            marginTop: 8,
            paddingTop: 8,
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {s.dealId && (
            <span
              className="mono"
              style={{
                fontSize: 10,
                color: COLORS.inkMute,
                background: "rgba(5,9,15,0.6)",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 4,
                padding: "2px 7px",
                letterSpacing: "0.05em",
              }}
            >
              {s.dealId}
            </span>
          )}
          {s.trigger && (
            <span
              className="mono"
              style={{
                fontSize: 10,
                color: COLORS.inkMute,
                letterSpacing: "0.04em",
              }}
            >
              ↳ {s.trigger}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function PackageDrawer({ pkg, onClose, isMobile, onRequestPackage }: PackageDrawerProps) {
  const Icon = pkg.icon;
  useEscapeKey(onClose);
  useLockBodyScroll(true);
  const { dragY, handlers } = useSwipeToDismiss(onClose);

  // Defensive guards in case package data is partially hydrated
  const signals = pkg.signals ?? [];
  const activation = pkg.activation ?? [];
  const verticals = pkg.verticals ?? [];

  const containerStyle = isMobile
    ? {
        width: "100%",
        maxHeight: "92vh",
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderTop: `1px solid ${COLORS.borderHi}`,
      }
    : {
        width: "min(640px, 100%)",
        height: "100%",
        borderLeft: `1px solid ${COLORS.borderHi}`,
      };

  const animClass = isMobile ? "slide-up" : "slide-right";

  const hasLayers = signals.some(s => s.layer);
  const layers: Array<"pre" | "live" | "post"> = ["pre", "live", "post"];
  const grouped = hasLayers
    ? layers.map(l => ({ layer: l, signals: signals.filter(s => s.layer === l) })).filter(g => g.signals.length > 0)
    : null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 110,
        background: "rgba(5, 9, 15, 0.7)",
        backdropFilter: "blur(8px)",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: isMobile ? "flex-end" : "stretch",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={animClass}
        style={{
          ...containerStyle,
          background: COLORS.bgCardSolid,
          overflowY: "auto",
          position: "relative",
          transform: isMobile && dragY ? `translateY(${dragY}px)` : undefined,
          transition: isMobile && !dragY ? "transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)" : undefined,
        }}
      >
        {isMobile && (
          <div
            {...handlers}
            onClick={onClose}
            role="button"
            aria-label="Close — drag down or tap"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 28,
              padding: "12px 0 8px",
              position: "sticky",
              top: 0,
              background: COLORS.bgCardSolid,
              zIndex: 5,
              cursor: "grab",
              touchAction: "none",
            }}
          >
            <div style={{ width: 40, height: 4, borderRadius: 2, background: COLORS.borderHi }} />
          </div>
        )}

        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 40,
            right: -120,
            width: 320,
            height: 320,
            opacity: 0.04,
            pointerEvents: "none",
            backgroundImage: `url('${ASSETS.badge}')`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
          }}
        />

        <div
          style={{
            background: `linear-gradient(180deg, ${pkg.accent}10 0%, transparent 100%)`,
            padding: isMobile ? "20px 20px 24px" : "32px 32px 28px",
            position: "relative",
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
            <div
              style={{
                width: isMobile ? 48 : 56,
                height: isMobile ? 48 : 56,
                borderRadius: 10,
                background: `${pkg.accent}15`,
                border: `1px solid ${pkg.accent}40`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 0 24px ${pkg.accent}33`,
              }}
            >
              <Icon size={isMobile ? 22 : 26} color={pkg.accent} strokeWidth={2} />
            </div>
            <button
              onClick={onClose}
              style={{
                background: "transparent",
                border: `1px solid ${COLORS.border}`,
                color: COLORS.ink,
                width: isMobile ? 44 : 36,
                height: isMobile ? 44 : 36,
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="mono" style={{ fontSize: 11, color: pkg.accent, letterSpacing: "0.15em", marginBottom: 8 }}>
            ◆ {pkg.name.toUpperCase()} PACKAGE
          </div>
          <h2
            className="display"
            style={{
              fontSize: isMobile ? 28 : 40,
              fontWeight: 700,
              margin: "0 0 10px",
              lineHeight: 1.05,
            }}
          >
            {pkg.tagline}
          </h2>
          <p
            style={{
              color: COLORS.inkDim,
              fontSize: isMobile ? 14 : 15,
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            {pkg.useCase}
          </p>
        </div>

        <div style={{ padding: isMobile ? 20 : 32, position: "relative" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1,
              background: `${COLORS.borderHi}40`,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 8,
              overflow: "hidden",
              marginBottom: 28,
            }}
          >
            {[
              { l: "Signals", v: pkg.signalCount },
              { l: "TTL range", v: pkg.ttlRange },
              { l: "Verticals", v: pkg.verticals.length },
            ].map((m, i) => (
              <div key={i} style={{ background: COLORS.bgCardSolid, padding: isMobile ? 12 : 16 }}>
                <div className="mono" style={{ fontSize: 9, color: COLORS.inkMute, letterSpacing: "0.1em", marginBottom: 6 }}>
                  {m.l.toUpperCase()}
                </div>
                <div className="mono" style={{ fontSize: isMobile ? 14 : 18, color: COLORS.ink, fontWeight: 600 }}>
                  {m.v}
                </div>
              </div>
            ))}
          </div>

          <Section title="Example signals">
            {grouped ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {grouped.map(({ layer, signals }) => (
                  <div key={layer}>
                    <div
                      className="mono"
                      style={{
                        fontSize: 9,
                        color: LAYER_COLORS[layer],
                        letterSpacing: "0.15em",
                        marginBottom: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: 16,
                          height: 1,
                          background: LAYER_COLORS[layer],
                          opacity: 0.5,
                        }}
                      />
                      {LAYER_LABELS[layer]}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {signals.map((s, i) => (
                        <SignalRow key={i} s={s} accent={pkg.accent} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {signals.map((s, i) => (
                  <SignalRow key={i} s={s} accent={pkg.accent} />
                ))}
              </div>
            )}
          </Section>

          <Section title="Activation paths">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {activation.map(a => (
                <div
                  key={a}
                  style={{
                    background: "rgba(15, 26, 44, 0.8)",
                    border: `1px solid ${pkg.accent}30`,
                    color: COLORS.ink,
                    padding: "8px 14px",
                    borderRadius: 6,
                    fontSize: 13,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Check size={12} color={pkg.accent} /> {a}
                </div>
              ))}
            </div>
          </Section>

          <Section title="Verticals served">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {verticals.map(v => (
                <span
                  key={v}
                  className="mono"
                  style={{
                    fontSize: 11,
                    padding: "5px 10px",
                    background: "rgba(15, 26, 44, 0.8)",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 4,
                    color: COLORS.inkDim,
                    letterSpacing: "0.05em",
                  }}
                >
                  {v.toUpperCase()}
                </span>
              ))}
            </div>
          </Section>

          <button
            onClick={onRequestPackage}
            style={{
              width: "100%",
              background: pkg.accent,
              color: COLORS.bg,
              border: "none",
              padding: "16px 20px",
              fontSize: 14,
              fontWeight: 700,
              borderRadius: 8,
              marginTop: 24,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              boxShadow: `0 8px 24px ${pkg.accent}40`,
              cursor: "pointer",
            }}
          >
            Request {pkg.name} package <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div className="mono" style={{ fontSize: 10, color: COLORS.inkMute, letterSpacing: "0.15em", marginBottom: 12 }}>
        {title.toUpperCase()}
      </div>
      {children}
    </div>
  );
}
